import React from 'react';
import PropTypes from 'prop-types';
import { TfiReload } from 'react-icons/tfi';
import BlocoSelector from '../BlocoSelector/BlocoSelector';
import './FeatureControlBar.css';

/**
 * Toolbar escalável para feature pages
 * 
 * Layout: [Título] [titleAddon] | [BlocoSelector ▼] [🔄 Refresh] [children...]
 * 
 * O prop `children` é um slot para ações futuras (export excel, filtros, etc.)
 * que serão renderizadas à direita dos controles built-in.
 * 
 * @param {string} [title] - Título da feature
 * @param {ReactNode} [titleAddon] - Elemento ao lado do título (ex.: StatusPill)
 * @param {Array} blocos - Lista de blocos pré-filtrados
 * @param {number|string} selectedBloco - Bloco selecionado
 * @param {Function} onBlocoChange - Callback: (blocoId: number) => void
 * @param {Function} [onRefresh] - Callback opcional para refresh
 * @param {boolean} [isRefreshing] - Estado de loading do refresh
 * @param {string} [refreshLabel] - Texto opcional do botão refresh
 * @param {ReactNode} [children] - Slot para ações extras (export, filtros, etc.)
 */
const FeatureControlBar = ({
    title,
    titleAddon,
    blocos,
    selectedBloco,
    onBlocoChange,
    onRefresh,
    isRefreshing = false,
    refreshLabel = '',
    children
}) => {
    return (
        <div className="feature-control-bar">
            {/* Esquerda - Título */}
            <div className="feature-bar__start">
                {title && <h2 className="feature-bar__title">{title}</h2>}
                {titleAddon}
            </div>
            
            {/* Direita - Controles + Ações */}
            <div className="feature-bar__end">
                <BlocoSelector
                    blocos={blocos}
                    selectedBloco={selectedBloco}
                    onBlocoChange={onBlocoChange}
                />

                {onRefresh && (
                    <button
                        className="feature-bar__refresh"
                        onClick={onRefresh}
                        disabled={isRefreshing || !selectedBloco}
                        title={refreshLabel || 'Atualizar'}
                    >
                        <TfiReload 
                            size={16} 
                            className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                        />
                        {refreshLabel && <span className="refresh-label">{refreshLabel}</span>}
                    </button>
                )}

                {/* Slot escalável para ações futuras */}
                {children && (
                    <div className="feature-bar__actions">
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};

FeatureControlBar.propTypes = {
    title: PropTypes.string,
    titleAddon: PropTypes.node,
    blocos: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            nome: PropTypes.string.isRequired
        })
    ).isRequired,
    selectedBloco: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onBlocoChange: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    isRefreshing: PropTypes.bool,
    refreshLabel: PropTypes.string,
    children: PropTypes.node
};

export default FeatureControlBar;
